import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

const PIN_BUTTON_SIZE = "22px";
const WORKSPACE_ROW_HEIGHT = "38px";

export const useStyles = makeStyles({
  sectionHeader: {
    padding: "8px 11px 6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
    overflow: "hidden",
    transition: `opacity ${designTokens.transition.fast}`,
  },
  sectionToggle: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    color: projectIqTokens.navSectionLabel,
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    ":hover": { color: projectIqTokens.sidebarItemText },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },
  sectionChevron: {
    display: "flex",
    transition: `transform ${designTokens.transition.fast}`,
  },
  sectionChevronCollapsed: {
    transform: "rotate(-90deg)",
  },
  countBadge: {
    minWidth: "18px",
    padding: "1px 6px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: projectIqTokens.addButtonBg,
    color: projectIqTokens.sidebarItemText,
    fontSize: "10.5px",
    fontWeight: "700",
    letterSpacing: 0,
    textAlign: "center",
  },
  addButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "22px",
    height: "22px",
    border: "none",
    borderRadius: "6px",
    padding: 0,
    backgroundColor: projectIqTokens.addButtonBg,
    color: projectIqTokens.addButtonText,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: projectIqTokens.addButtonBgHover,
    },
    ":active": {
      backgroundColor: projectIqTokens.addButtonBgActive,
    },
    ":focus-visible": {
      outline: `1px solid ${projectIqTokens.sidebarAccent}`,
    },
  },
  workspaceList: {
    flexShrink: 0,
    overflowX: "hidden",
    padding: "4px 0",
    display: "flex",
    flexDirection: "column",
  },
  sectionEmpty: {
    padding: "4px 11px 8px",
    color: projectIqTokens.sidebarDimText,
    fontSize: "12px",
  },
  workspaceListEmpty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: `${designTokens.spacing[6]} ${designTokens.spacing[4]}`,
    color: projectIqTokens.sidebarMuted,
    fontSize: designTokens.fontSize.sm,
    textAlign: "center",
  },
  workspaceItem: {
    flexShrink: 0,
    boxSizing: "border-box",
    height: WORKSPACE_ROW_HEIGHT,
    width: "100%",
    textAlign: "left" as const,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "8px 16px",
    borderLeft: "3px solid transparent",
    borderRadius: 0,
    color: projectIqTokens.sidebarItemText,
    cursor: "pointer",
    userSelect: "none",
    background: "transparent",
    overflow: "hidden",
    ":hover": { backgroundColor: projectIqTokens.sidebarHover },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },
  workspaceItemCollapsed: {
    justifyContent: "center",
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
    width: "100%",
  },
  workspaceItemSelected: {
    backgroundColor: projectIqTokens.sidebarActiveBg,
    borderLeft: `3px solid ${projectIqTokens.teal2}`,
    ":hover": { backgroundColor: projectIqTokens.sidebarActiveBg },
  },
  workspaceDot: {
    width: "7px",
    height: "7px",
    borderRadius: designTokens.borderRadius.full,
    flexShrink: 0,
    backgroundColor: projectIqTokens.sidebarDimText,
  },
  workspaceItemNameSelected: {
    color: designTokens.neutral.white,
  },
  workspaceItemName: {
    flex: "1 1 0%",
    minWidth: 0,
    fontSize: "13px",
    fontWeight: designTokens.fontWeight.normal,
    color: projectIqTokens.sidebarItemText,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pinBtn: {
    width: PIN_BUTTON_SIZE,
    height: PIN_BUTTON_SIZE,
    minWidth: PIN_BUTTON_SIZE,
    borderRadius: "7px",
    border: "none",
    cursor: "pointer",
    flexShrink: 0,
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    marginRight: "4px",
  },
  pinBtnPinned: {
    display: "flex",
    background: projectIqTokens.pinPinnedBg,
    color: projectIqTokens.teal,
  },
  pinBtnUnpinned: {
    color: projectIqTokens.sidebarFadedText,
    background: projectIqTokens.pinUnpinnedBg,
  },
  pinBtnVisible: {
    display: "flex",
  },
  workspaceInitialsAvatar: {
    width: projectIqTokens.sidebarAvatarSize,
    height: projectIqTokens.sidebarAvatarSize,
    minWidth: projectIqTokens.sidebarAvatarSize,
    borderRadius: projectIqTokens.sidebarAvatarRadius,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: projectIqTokens.sidebarAvatarFontSize,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.sidebarAvatarInactiveText,
    letterSpacing: "0.3px",
    position: "relative",
    flexShrink: 0,
    background: "transparent",
  },
  workspaceInitialsAvatarSelected: {
    background: "transparent",
    color: designTokens.neutral.white,
  },
  collapsedPinIndicator: {
    position: "absolute",
    top: "-1px",
    left: "4px",
    color: projectIqTokens.teal,
    pointerEvents: "none",
  },
  viewAllLink: {
    alignSelf: "flex-start",
    marginTop: designTokens.spacing[2],
    padding: "4px 11px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: projectIqTokens.teal2,
    fontSize: "12.5px",
    fontWeight: "600",
    ":hover": { textDecoration: "underline" },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
    },
  },
  skeletonRow: {
    display: "flex",
    alignItems: "center",
    height: "38px",
    padding: "0 19px",
  },
  skeletonRowCollapsed: {
    justifyContent: "center",
    height: "auto",
    padding: `${designTokens.spacing[1]} 0`,
  },
  skeletonAvatar: {
    width: projectIqTokens.sidebarAvatarSize,
    height: projectIqTokens.sidebarAvatarSize,
    borderRadius: projectIqTokens.sidebarAvatarRadius,
  },
  skeletonWrapper: {
    width: "100%",
  },
  skeletonName: {
    height: "13px",
    borderRadius: "4px",
    width: "68%",
  },
});
